import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { formatAmount, isValidAmount } from '@goaltree/backend/convex/lib/habitMeasure'
import { cn } from '@goaltree/ui/lib/utils'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { TrendingUp, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useHabitMeasure } from '@/components/number-habit'
import { Toggle } from '@/components/settings/primitives'
import { logicalDayStr } from '@/lib/day'
import { haptic } from '@/lib/haptics'

// Progressive overload for number habits: suggest a higher target once the
// current one is easy. Raises start tomorrow; nothing here ever pushes.

type Suggestion = NonNullable<
  FunctionReturnType<typeof api.habitLogs.levelUpForHabit>
>['suggestion'] & {}

const amt = (n: number, unit: string, lang: string) => `${formatAmount(n, lang)} ${unit}`.trim()

type LevelUpInfo = NonNullable<FunctionReturnType<typeof api.habitLogs.levelUpForHabit>>

function patchLevelUp(
  store: OptimisticLocalStore,
  habitId: Id<'nodes'>,
  patch: (info: LevelUpInfo) => Partial<LevelUpInfo>,
  dropReady: boolean,
) {
  const info = store.getQuery(api.habitLogs.levelUpForHabit, { habitId })
  if (info) store.setQuery(api.habitLogs.levelUpForHabit, { habitId }, { ...info, ...patch(info) })
  if (!dropReady) return
  const overview = store.getQuery(api.habitLogs.levelUpOverview, {})
  if (overview)
    store.setQuery(
      api.habitLogs.levelUpOverview,
      {},
      {
        ...overview,
        ready: overview.ready.filter((r) => r.habitId !== habitId),
      },
    )
}

export function useLevelUpActions() {
  const { t, i18n } = useTranslation()
  const accept = useMutation(api.habitLogs.acceptTargetBump).withOptimisticUpdate(
    (store, { habitId, newTarget }) =>
      patchLevelUp(
        store,
        habitId,
        () => ({
          suggestion: null,
          snoozedUntil: null,
          next: { target: newTarget, on: logicalDayStr(Date.now() + 86_400_000) },
        }),
        true,
      ),
  )
  const snooze = useMutation(api.habitLogs.snoozeTargetBump).withOptimisticUpdate(
    (store, { habitId }) => patchLevelUp(store, habitId, () => ({ suggestion: null }), true),
  )
  const maintain = useMutation(api.habitLogs.setMaintaining).withOptimisticUpdate(
    (store, { habitId, maintaining }) =>
      patchLevelUp(
        store,
        habitId,
        () =>
          maintaining
            ? { maintaining, suggestion: null, auto: null }
            : { maintaining, snoozedUntil: null },
        maintaining,
      ),
  )
  const fail = () => toast.error(t('levelUp.error'))
  return {
    bump: (habitId: Id<'nodes'>, to: number, unit: string) => {
      haptic('success')
      void accept({ habitId, newTarget: to })
        .then(() => toast.success(t('levelUp.bumped', { amount: amt(to, unit, i18n.language) })))
        .catch(fail)
    },
    snooze: (habitId: Id<'nodes'>) => void snooze({ habitId }).catch(fail),
    maintain: (habitId: Id<'nodes'>, maintaining: boolean) =>
      void maintain({ habitId, maintaining }).catch(fail),
  }
}

function suggestionText(
  t: ReturnType<typeof useTranslation>['t'],
  s: Suggestion,
  unit: string,
  lang: string,
) {
  const vars = {
    target: formatAmount(s.from, lang),
    typical: amt(s.typical, unit, lang),
    to: amt(s.to, unit, lang),
    unit,
    days: s.streak,
    hits: s.hits,
    window: s.window,
  }
  if (!s.overshoot) return t('levelUp.steady', vars)
  return s.streak >= s.window ? t('levelUp.overshootStreak', vars) : t('levelUp.overshoot', vars)
}

const btn =
  'cursor-pointer rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'
const primaryBtn = cn(btn, 'bg-primary text-primary-foreground hover:bg-primary/90')
const ghostBtn = cn(btn, 'text-muted-foreground hover:bg-muted hover:text-foreground')

// Habit detail: the suggestion card plus the auto-increase / maintaining settings.
export function LevelUpSection({ habitId }: { habitId: Id<'nodes'> }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const info = useQuery(api.habitLogs.levelUpForHabit, { habitId })
  const p = useHabitMeasure(habitId)
  const actions = useLevelUpActions()
  if (!info?.autoable || !p) return null
  const unit = p.measure.unit
  const s = info.suggestion
  const showTarget = info.growable && p.measure.direction === 'atLeast'
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {t('levelUp.section')}
      </span>
      {showTarget && s && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5">
          <p className="flex gap-2 text-xs leading-snug text-foreground">
            <TrendingUp className="mt-px size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
            {suggestionText(t, s, unit, lang)}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1">
            <button
              type="button"
              className={primaryBtn}
              onClick={() => actions.bump(habitId, s.to, unit)}
            >
              {t('levelUp.bump', { amount: formatAmount(s.to, lang) })}
            </button>
            <button type="button" className={ghostBtn} onClick={() => actions.snooze(habitId)}>
              {t('levelUp.notNow')}
            </button>
            <button
              type="button"
              className={ghostBtn}
              onClick={() => actions.maintain(habitId, true)}
            >
              {t('levelUp.maintaining')}
            </button>
          </div>
        </div>
      )}
      {info.next && (
        <p className="flex items-center gap-1.5 rounded-xl border border-border/60 px-3 py-2 text-xs text-foreground">
          <TrendingUp className="size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
          {t('levelUp.pending', { amount: amt(info.next.target, unit, lang) })}
        </p>
      )}
      <div className="flex flex-col divide-y divide-border/60 rounded-xl border border-border/60">
        <AutoIncreaseRow
          habitId={habitId}
          unit={unit}
          auto={info.auto}
          defaults={{ step: info.defaultStep, everyDays: info.defaultEveryDays }}
          defaultMinHitPct={info.defaultMinHitPct}
          down={p.measure.direction === 'atMost'}
          disabled={info.maintaining}
        />
        {showTarget && (
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground">{t('levelUp.maintaining')}</p>
              <p className="text-[11px] text-muted-foreground">{t('levelUp.maintainingDesc')}</p>
            </div>
            <Toggle
              checked={info.maintaining}
              label={t('levelUp.maintaining')}
              onChange={(on) => actions.maintain(habitId, on)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const numInput =
  'w-14 min-w-0 rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-xs tabular-nums text-foreground outline-none focus:border-primary/60'

type AutoInfo = NonNullable<LevelUpInfo['auto']>
type AutoSettings = { step: number; everyDays: number; cap?: number | null; minHitPct?: number }

function AutoIncreaseRow({
  habitId,
  unit,
  auto,
  defaults,
  defaultMinHitPct,
  down,
  disabled,
}: {
  habitId: Id<'nodes'>
  unit: string
  auto: AutoInfo | null
  defaults: { step: number; everyDays: number }
  defaultMinHitPct: number
  down: boolean
  disabled: boolean
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const setAuto = useMutation(api.habitLogs.setAutoProgress).withOptimisticUpdate(
    (store, { habitId, settings }) =>
      patchLevelUp(
        store,
        habitId,
        (info) =>
          settings
            ? {
                auto: {
                  step: settings.step,
                  everyDays: Math.round(settings.everyDays),
                  cap: settings.cap ?? null,
                  minHitPct: settings.minHitPct ?? defaultMinHitPct,
                  last: info.auto?.last ?? null,
                },
                maintaining: false,
              }
            : { auto: null },
        false,
      ),
  )
  const current = auto ?? { ...defaults, cap: null, minHitPct: defaultMinHitPct, last: null }
  const [step, setStep] = useState(String(current.step))
  const [cap, setCap] = useState(current.cap === null ? '' : String(current.cap))
  const [pct, setPct] = useState(String(current.minHitPct))
  useEffect(() => {
    setStep(String(current.step))
    setCap(current.cap === null ? '' : String(current.cap))
    setPct(String(current.minHitPct))
  }, [current.step, current.cap, current.minHitPct])
  const save = (settings: AutoSettings | null) =>
    void setAuto({ habitId, settings }).catch(() => toast.error(t('levelUp.error')))
  const commit = () => {
    const n = Number(step.replace(',', '.'))
    const c = cap.trim() === '' ? null : Number(cap.replace(',', '.'))
    const p = Math.round(Number(pct))
    if (!auto || !isValidAmount(n) || (c !== null && !isValidAmount(c)) || !(p >= 1 && p <= 100)) {
      setStep(String(current.step))
      setCap(current.cap === null ? '' : String(current.cap))
      setPct(String(current.minHitPct))
      return
    }
    if (n !== auto.step || c !== auto.cap || p !== auto.minHitPct)
      save({ step: n, everyDays: auto.everyDays, cap: c, minHitPct: p })
  }
  const sign = down ? '-' : '+'
  const rule = auto
    ? [
        t(
          auto.everyDays === 7
            ? 'levelUp.ruleWeekly'
            : auto.everyDays === 14
              ? 'levelUp.ruleBiweekly'
              : 'levelUp.ruleDays',
          { step: `${sign}${amt(auto.step, unit, lang)}`, count: auto.everyDays },
        ),
        auto.cap !== null ? t('levelUp.ruleCap', { cap: amt(auto.cap, unit, lang) }) : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null
  const last = auto?.last
  const lastDate = last
    ? new Date(`${last.on}T12:00:00Z`).toLocaleDateString(lang, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : ''
  return (
    <div className="px-3 py-2">
      <div className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground">{t('levelUp.auto')}</p>
          <p className="text-[11px] text-muted-foreground">{rule ?? t('levelUp.autoDesc')}</p>
        </div>
        <Toggle
          checked={!!auto}
          disabled={disabled}
          label={t('levelUp.auto')}
          onChange={(on) =>
            save(
              on
                ? {
                    step: current.step,
                    everyDays: current.everyDays,
                    cap: current.cap,
                    minHitPct: current.minHitPct,
                  }
                : null,
            )
          }
        />
      </div>
      {auto && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          {t('levelUp.autoBy')}
          <input
            inputMode="decimal"
            aria-label={t('levelUp.autoBy')}
            value={step}
            onChange={(e) => setStep(e.target.value.replace(/[^\d.,]/g, '').slice(0, 7))}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={numInput}
          />
          {unit} {t('levelUp.autoEvery')}
          <select
            aria-label={t('levelUp.autoEvery')}
            value={auto.everyDays === 14 ? 14 : 7}
            onChange={(e) =>
              save({
                step: auto.step,
                everyDays: Number(e.target.value),
                cap: auto.cap,
                minHitPct: auto.minHitPct,
              })
            }
            className={cn(numInput, 'w-auto')}
          >
            <option value={7}>{t('levelUp.autoWeek')}</option>
            <option value={14}>{t('levelUp.autoWeeks')}</option>
          </select>
          <span className="basis-full" />
          {t('levelUp.autoCap')}
          <input
            inputMode="decimal"
            aria-label={t('levelUp.autoCap')}
            placeholder={t('levelUp.autoCapNone')}
            value={cap}
            onChange={(e) => setCap(e.target.value.replace(/[^\d.,]/g, '').slice(0, 7))}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={numInput}
          />
          <span className="basis-full" />
          {t('levelUp.autoMinHit')}
          <input
            inputMode="numeric"
            aria-label={t('levelUp.autoMinHit')}
            value={pct}
            onChange={(e) => setPct(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={numInput}
          />
          {t('levelUp.autoMinHitUnit')}
          {last && (
            <p className="basis-full pt-1 text-foreground">
              {t(last.held ? 'levelUp.lastHeld' : 'levelUp.lastRaised', {
                from: amt(last.from, unit, lang),
                to: amt(last.to, unit, lang),
                date: lastDate,
                hits: last.hits,
                total: last.total,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

const RAISED_KEY = 'levelUp.raisedSeen'
const readSeen = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(RAISED_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

// Today: the strongest habit that's ready and already done today, plus notes
// for automatic raises that start today. At most one nudge at a time.
export function TodayLevelUp({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const data = useQuery(api.habitLogs.levelUpOverview)
  const actions = useLevelUpActions()
  const [seen, setSeen] = useState(readSeen)
  const nudge = data?.ready.find((r) => r.suggestion.doneToday)
  const raised = (data?.raised ?? []).filter((r) => !seen.includes(`${r.habitId}:${r.to}`))
  if (!nudge && !raised.length) return null
  const dismissRaised = (key: string) => {
    const next = [...seen, key].slice(-30)
    setSeen(next)
    try {
      localStorage.setItem(RAISED_KEY, JSON.stringify(next))
    } catch {}
  }
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {raised.map((r) => (
        <p
          key={r.habitId}
          className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/40 py-1.5 pr-1.5 pl-3 text-xs text-foreground"
        >
          <TrendingUp className="size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
          <span className="min-w-0 flex-1">
            {t('levelUp.raised', { title: r.title, amount: amt(r.to, r.unit, lang) })}
          </span>
          <button
            type="button"
            aria-label={t('levelUp.dismiss')}
            onClick={() => dismissRaised(`${r.habitId}:${r.to}`)}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </p>
      ))}
      {nudge && (
        <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 py-2 pr-1.5 pl-3">
          <TrendingUp className="mt-0.5 size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
          <div className="min-w-0 flex-1">
            <p className="text-xs leading-snug text-foreground">
              {t('levelUp.todayNudge', {
                target: amt(nudge.suggestion.from, nudge.unit, lang),
                to: amt(nudge.suggestion.to, nudge.unit, lang),
                title: nudge.title,
              })}
            </p>
            <div className="mt-1.5 -ml-3 flex flex-wrap items-center gap-1 pl-2">
              <button
                type="button"
                className={primaryBtn}
                onClick={() => actions.bump(nudge.habitId, nudge.suggestion.to, nudge.unit)}
              >
                {t('levelUp.bumpShort')}
              </button>
              <button
                type="button"
                className={ghostBtn}
                onClick={() => actions.snooze(nudge.habitId)}
              >
                {t('levelUp.notNow')}
              </button>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('levelUp.notNow')}
            onClick={() => actions.snooze(nudge.habitId)}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
